import { ThumbnailVariant } from "@obg/gaming.config";

export interface HomePage {
	/**
	 * Enabled or disables the north star home page
	 */
	isEnabled?: boolean;
	/**
	 * Allows or disallows the north star home page
	 */
	isAllowed?: boolean;
	/**
	 * External promotions lobby URL CRS key
	 */
	externalPromotionsLobbyUrlKey?: string;
	/**
	 * The order of the components that will be rendered
	 */
	componentOrder?: {
		/**
		 * The default order of the components that will be rendered
		 */
		defaultMarket?: HomePageWidget[];
		/**
		 * The market specific order of the components that will be rendered
		 */
		marketSpecific?: Record<string, HomePageWidget[]>;
	};
	/**
	 * Parameters for setting up north star promotion.
	 */
	promotionLobby?: {
		/**
		 * Fallback image for promotion
		 */
		fallbackImage?: {
			/**
			 * Fallback image URL for promotion
			 */
			url: string;
		};
	};
	sponsorshipBanners?: SponsorshipBannerConfig[];
	registrationStart?: RegistrationStart;

	mainBanner?: MainBannerConfig;
	promotionSection?: HomePromotionWidget;
	playWithUsSection?: PlayWithUsSectionConfig;
	reassuranceSection?: ReassuranceSectionConfig;

	/**
	 * Action Banner configuration
	 */
	actionBanner?: ActionBannerConfig;

	/**
	 * enabled Web Components
	 */
	enabledWebComponents?: string[];

	/**
	 * Custom parameters for the home page preview section
	 */
	gamingPreviewSection?: HomePageGamingPreview;

	/**
	 * Enables or disables main banner for logged out user, if disabled action banner will be shown
	 */
	showMainBannerForLoggedOutUser?: boolean;

	/**
	 * Enables or disables REALM payment method logos
	 */
	alternatePaymentIconsStyle?: boolean;

	/**
	 * Homepage Fabric configuration
	 */
	homePageFabric?: HomePageFabricConfig;
}

export interface HomePageWidget {
	/**
	 * Enables or disables widget on home page
	 */
	isEnabled: boolean;
	/**
	 * Order of a widget in home page
	 */
	order: number;
	/**
	 * Widget name which should be displayed
	 */
	label: string;
	/**
	 * Specific CRS key to get content
	 */
	documentKey?: string;
	/**
	 * Widget name which should be displayed
	 */
	widgetConfig?: HomeWidgetConfig;
	/**
	 * Define the type of component in the case the component has any
	 */
	componentType?: string;
}

export interface HomeWidgetConfig {
	/**
	 * Defines visibility of an items in a component
	 */
	itemPreview?: {
		oneRowThreshold: number;
		twoRowThreshold: number;
	};
}

export interface MainBannerConfig {
	/**
	 * Enables or disables widget on home page
	 */
	isHidden: boolean;
	/**
	 * The specific market specify where banner is enabled or disabled based on above property
	 * Note: if specificMarket is empty, then shows or hidden on all brands based on above property
	 */
	specificMarket?: string[];
}

export interface HomePromotionWidget {
	isTermsAndConditionsEnabled: boolean;
}

export interface PlayWithUsSectionConfig {
	/**
	 * Enable or disable play with us section web component.
	 * if enabled: display stencil component
	 * else: show angular component
	 */
	isPlayWithUsSectionWebComponentEnabled: boolean;
}

export interface ReassuranceSectionConfig {
	/**
	 * Enable or disable reassurance section web component.
	 * if enabled: display stencil component
	 * else: show angular component
	 */
	isReassuranceSectionWebComponentEnabled: boolean;
}

export interface SponsorshipBannerConfig {
	/**
	 * Enables stencil sponsorship banner
	 */
	isEnabled: boolean;
	/**
	 * Matches the banner type from CRS
	 * Note: This type strictly follows the CRS variable "bannerType" at the SponsorshipBanner Document
	 */
	bannerType: SponsorshipBannerType;
	/**
	 * The specific market specify where banner is enabled
	 * Note: if specificMarket is empty, then shows on all brands
	 */
	specificMarket?: string[];
	isEnabledCTA?: boolean;
}

/**
 * With the following structure in the config is possible to customize the elements of the Gaming section in the Home Page
 */
export interface HomePageGamingPreview {
	/**
	 * The parent collection name which contains the subcollections order
	 */
	parentCollectionName?: string;

	/**
	 * Game collection of the preview section
	 */
	gameCollections?: Record<string, HomePageGameCollection>;
}

export interface HomePageGameCollection {
	/**
	 * Game collection id from gamingExternal.gameCollection.gameCollections
	 */
	collectionId: string;

	/**
	 * The thumbnail variant of the preview element
	 */
	thumbnailVariant: ThumbnailVariant;

	/**
	 * Number of the preview element to one row (default: 6)
	 */
	oneRowThreshold?: number;

	/**
	 * Number of the preview element to two row (default: 12)
	 */
	twoRowThreshold?: number;
}

/**
 * Note: Those values should match exactly the values of the "bannerType" attribute from CRS for the SponsorshipBanner document
 */
export enum SponsorshipBannerType {
	main = "main",
	highlight1 = "highlight1",
	highlight2 = "highlight2",
}

export enum RegistrationStart {
	BankIdFlow = "showBankIdFlow",
	OnlyRegistrationBtn = "showOnlyRegistrationBtn",
	InlineForm = "showInlineForm",
	CPRNumberFlow = "showCPRNumberFlow",
}

export interface ActionBannerConfig {
	/**
	 * Define primary field for action banner form
	 */
	primeField?: ActionBannerPrimeField;
	/**
	 * Hide registration banner
	 */
	hideRegistrationBanner?: boolean;
}

export enum ActionBannerPrimeField {
	personNumber = "personNumber",
	email = "email",
}

export interface HomePageFabricConfig {
	/**
	 * Enable or disable the homepage fabric.
	 */
	isHomePageFabricEnabled: boolean;
}
